import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Platform } from 'react-native';

export interface AuthUser {
  id: string;
  email: string;
  role: 'coach' | 'guardian' | 'athlete' | 'member';
  first_name: string;
  last_name: string;
  dob: string | null;
  terms_accepted_at: string | null;
  join_date: string;
  is_active: boolean;
  profile_picture_url: string | null;
  weight_kg: number | null;
  height_cm: number | null;
  measurement_unit: string | null;
  created_at: string;
  updated_at: string;
}

export interface VietQRPaymentData {
  payment_id: string;
  order_number: string;
  amount: number;
  qr_url: string;
  bank_bin: string;
  account_number: string;
  account_name: string;
  user_marked_paid_at?: string | null;
  order_items?: VietQROrderItem[];
}

export interface VietQROrderItem {
  label: string;
  description?: string;
  subtotal: number | string;
  discount_amount: number | string;
  final_amount: number | string;
  term_months?: number;
}

export interface PendingMember {
  userId: string | null; // null for new sub-users not yet created
  firstName?: string;
  lastName?: string;
  dob: string | null;
  planId: string | null;
  termMonths?: number;
  weight_kg?: number | null;
  height_cm?: number | null;
  measurement_unit?: 'metric' | 'imperial';
}

export interface Plan {
  id: string;
  machine_name: string;
  price: number;
  billing_cycle: string;
}

export interface Membership {
  id: string;
  user_id: string;
  plan_id: string;
  plan?: Plan | null;
  status: 'active' | 'paused' | 'expired' | 'cancelled' | 'pending_payment';
  start_date: string;
  end_date: string | null;
  renewal_term_months?: number;
  billing_details?: {
    line_amount?: string;
    discount_amount?: string;
    final_amount?: string;
  } | null;
  active_pause?: {
    paused_start_date: string;
    paused_end_date: string | null;
    pause_reason: string;
    paused_by_name?: string;
  } | null;
}

export interface ActiveMembershipMember {
  user_id: string;
  user_name: string;
  membership: Membership;
}

export interface SubscriptionFormState {
  mainPlanId: string | null;
  isSkipping: boolean;
  members: PendingMember[];
  removedSubUserIds: string[];
  step?: 1 | 2;
  referralCode?: string;
  appliedReferralCode?: string | null;
  /** Per-member term selection: userId → termMonths */
  memberTermMonths?: Record<string, number>;
  /** Whether user has explicitly decided about adding family members */
  familyDecisionMade?: boolean;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  needsOnboarding: boolean;
  needsSubscription: boolean;
  emailVerified: boolean;
  pending_invite_token: string | null;
  activeVietQRPayment: VietQRPaymentData | null;
  subscriptionForm: SubscriptionFormState | null;
  pendingVerificationPaymentId: string | null;
  /** Payment ID of an order the user can re-use (update in-place) on the next Pay click,
   *  instead of creating a brand-new order. Set when the user goes back from the VietQR
   *  screen; cleared when the payment is completed/verified or the user logs out. */
  reusablePaymentId: string | null;
  showActivationSuccessBanner: boolean;
  hasPausedMembership: boolean;
  pausedMembership: ActiveMembershipMember | null;
  needsLegalAgreements: boolean;
  subUserBlocked: boolean;
  pickupSetupPrompted: boolean;
  setAuth: (token: string, user: AuthUser, needsOnboarding?: boolean, emailVerified?: boolean) => void;
  setHasPausedMembership: (value: boolean, membership?: ActiveMembershipMember | null) => void;
  setNeedsLegalAgreements: (value: boolean) => void;
  setSubUserBlocked: (value: boolean) => void;
  setPickupSetupPrompted: (value: boolean) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<AuthUser>) => void;
  setNeedsOnboarding: (value: boolean) => void;
  setNeedsSubscription: (value: boolean) => void;
  setEmailVerified: (value: boolean) => void;
  setPendingInviteToken: (token: string | null) => void;
  setActiveVietQRPayment: (data: VietQRPaymentData | null) => void;
  setSubscriptionForm: (data: SubscriptionFormState | null) => void;
  clearSubscriptionForm: () => void;
  setPendingVerificationPaymentId: (id: string | null) => void;
  setReusablePaymentId: (id: string | null) => void;
  setShowActivationSuccessBanner: (value: boolean) => void;
  hydrated: boolean;
  setHydrated: (value: boolean) => void;
}

// Platform-appropriate storage: SecureStore for native, localStorage for web
const isWeb = Platform.OS === 'web';

const secureStorage = isWeb
  ? {
      getItem: async (name: string): Promise<string | null> => {
        try {
          return localStorage.getItem(name);
        } catch {
          return null;
        }
      },
      setItem: async (name: string, value: string): Promise<void> => {
        try {
          localStorage.setItem(name, value);
        } catch {}
      },
      removeItem: async (name: string): Promise<void> => {
        try {
          localStorage.removeItem(name);
        } catch {}
      },
    }
  : {
      getItem: async (name: string): Promise<string | null> => {
        const SecureStore = await import('expo-secure-store');
        return await SecureStore.getItemAsync(name);
      },
      setItem: async (name: string, value: string): Promise<void> => {
        const SecureStore = await import('expo-secure-store');
        await SecureStore.setItemAsync(name, value);
      },
      removeItem: async (name: string): Promise<void> => {
        const SecureStore = await import('expo-secure-store');
        await SecureStore.deleteItemAsync(name);
      },
    };

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      needsOnboarding: false,
      needsSubscription: false,
      emailVerified: false,
      pending_invite_token: null,
      activeVietQRPayment: null,
      subscriptionForm: null,
      pendingVerificationPaymentId: null,
      reusablePaymentId: null,
      showActivationSuccessBanner: false,
      hasPausedMembership: false,
      pausedMembership: null,
      needsLegalAgreements: false,
      subUserBlocked: false,
      pickupSetupPrompted: false,
      hydrated: false,

      setAuth: (token, user, needsOnboarding = false, emailVerified = true) =>
        set({
          token,
          user,
          isAuthenticated: true,
          needsOnboarding,
          emailVerified,
        }),

      clearAuth: () =>
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          needsOnboarding: false,
          needsSubscription: false,
          emailVerified: false,
          pending_invite_token: null,
          activeVietQRPayment: null,
          subscriptionForm: null,
          pendingVerificationPaymentId: null,
          reusablePaymentId: null,
          showActivationSuccessBanner: false,
          hasPausedMembership: false,
          pausedMembership: null,
          needsLegalAgreements: false,
          subUserBlocked: false,
          pickupSetupPrompted: false,
        }),

      updateUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
        })),

      setNeedsOnboarding: (value) =>
        set({ needsOnboarding: value }),

      setNeedsSubscription: (value) =>
        set({ needsSubscription: value }),

      setEmailVerified: (value) =>
        set({ emailVerified: value }),

      setPendingInviteToken: (token) =>
        set({ pending_invite_token: token }),

      setActiveVietQRPayment: (data) =>
        set({ activeVietQRPayment: data }),

      setSubscriptionForm: (data) =>
        set({ subscriptionForm: data }),

      clearSubscriptionForm: () =>
        set({ subscriptionForm: null }),

      setPendingVerificationPaymentId: (id) =>
        set({ pendingVerificationPaymentId: id }),

      setReusablePaymentId: (id) =>
        set({ reusablePaymentId: id }),

      setShowActivationSuccessBanner: (value) =>
        set({ showActivationSuccessBanner: value }),

      setHasPausedMembership: (value, membership = null) =>
        set({ hasPausedMembership: value, pausedMembership: membership }),

      setNeedsLegalAgreements: (value) =>
        set({ needsLegalAgreements: value }),

      setSubUserBlocked: (value) =>
        set({ subUserBlocked: value }),

      setPickupSetupPrompted: (value) =>
        set({ pickupSetupPrompted: value }),

      setHydrated: (value) =>
        set({ hydrated: value }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => secureStorage),
      onRehydrateStorage: () => () => {
        useAuthStore.getState().setHydrated(true);
      },
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        needsOnboarding: state.needsOnboarding,
        needsSubscription: state.needsSubscription,
        emailVerified: state.emailVerified,
        pending_invite_token: state.pending_invite_token,
        activeVietQRPayment: state.activeVietQRPayment,
        subscriptionForm: state.subscriptionForm,
        pendingVerificationPaymentId: state.pendingVerificationPaymentId,
        reusablePaymentId: state.reusablePaymentId,
        showActivationSuccessBanner: state.showActivationSuccessBanner,
        hasPausedMembership: state.hasPausedMembership,
        pausedMembership: state.pausedMembership,
        needsLegalAgreements: state.needsLegalAgreements,
        subUserBlocked: state.subUserBlocked,
        pickupSetupPrompted: state.pickupSetupPrompted,
      }),
    }
  )
);
